import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { getMe } from '@/api/auth';
import { useFydUser } from '@/store/auth';
import Eyebrow from '@/ui/atoms/Eyebrow';
import Button from '@/ui/atoms/Button';
import Spinner from '@/ui/atoms/Spinner';

// Post-auth business gate per auth-billing.m0.md §1.1 / frontend.m0.md §1.
// PR-UX-10 Doherty · spinner shown immediately (≤16ms), feedback within 100ms always.
// Progressive backoff: 1s, 2s, 5s, 5s, 10s (Nielsen 0.1·1·10s — give system time to recover
// without making user wait beyond perception thresholds).
type GateState =
  | { kind: 'clerk_loading' }
  | { kind: 'me_loading'; attempt: number }
  | { kind: 'me_preparing'; attempt: number }
  | { kind: 'me_exhausted' }       // terminal · all attempts spent (PR-UX-10 error.undo_required)
  | { kind: 'me_error'; message: string }
  | { kind: 'ready' };

const BACKOFF_MS = [1_000, 2_000, 5_000, 5_000, 10_000]; // total ~23s
const MAX_ATTEMPTS = BACKOFF_MS.length + 1;              // 6

export default function AuthCallback() {
  const { t } = useTranslation();
  const { isLoaded, isSignedIn } = useClerkAuth();
  const setMe = useFydUser((s) => s.setMe);
  const [state, setState] = useState<GateState>({ kind: 'clerk_loading' });
  const [params] = useSearchParams();
  // Clerk's internal routing drops query params, so post-auth intent
  // persists via sessionStorage (set by Pricing, etc.). URL param wins
  // when both are present (explicit > implicit).
  const stashed = (typeof window !== 'undefined'
    ? window.sessionStorage.getItem('fyd_post_auth_redirect')
    : null) || '';
  const redirectTarget = params.get('redirect_url') || stashed || '/dashboard';

  useEffect(() => {
    if (!isLoaded) return;
    let cancelled = false;
    let timer: number | undefined;

    const fetchMe = async (attempt: number) => {
      setState({ kind: 'me_loading', attempt });
      try {
        const me = await getMe();
        if (cancelled) return;
        setMe(me);
        setState({ kind: 'ready' });
      } catch (err) {
        if (cancelled) return;
        // iter_12 fix: any error here is transient. AuthCallback is the
        // post-Clerk-success page; the !isSignedIn guard below has already
        // redirected anyone who isn't authenticated. Whatever the /me call
        // returns (401, 403, 422, network), retry-with-backoff until BE
        // catches up. iter_10's status whitelist still left 403/422/CORS
        // instantly fatal which surfaced "无法加载你的账户" without retry.
        if (attempt < MAX_ATTEMPTS) {
          setState({ kind: 'me_preparing', attempt });
          const delay = BACKOFF_MS[attempt - 1] ?? 10_000;
          timer = window.setTimeout(() => fetchMe(attempt + 1), delay);
        } else {
          // Terminal: do not leave UI in indefinite "preparing" state.
          setState({ kind: 'me_exhausted' });
        }
      }
    };

    fetchMe(1);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [isLoaded, setMe]);

  if (isLoaded && !isSignedIn) {
    return <Navigate to={`/auth?redirect_url=${encodeURIComponent(redirectTarget)}`} replace />;
  }
  if (state.kind === 'ready') {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem('fyd_post_auth_redirect');
    }
    return <Navigate to={redirectTarget} replace />;
  }

  return (
    <div className="max-w-md mx-auto px-8 py-32 text-center">
      {state.kind === 'clerk_loading' || state.kind === 'me_loading' ? (
        <>
          <Spinner size={28} className="mb-5 text-ink-2" label={t('callback.loading')} />
          <Eyebrow tone="ash" className="mb-3">{t('callback.signingIn')}</Eyebrow>
          <h1 className="font-serif text-3xl font-medium">{t('callback.loading')}</h1>
        </>
      ) : state.kind === 'me_preparing' ? (
        <>
          <Eyebrow tone="azure" className="mb-3">{t('callback.preparingTag')}</Eyebrow>
          <h1 className="font-serif text-3xl font-medium mb-3">
            {t('callback.preparingTitle')}
          </h1>
          <p className="text-base text-ink-2 mb-6">{t('callback.preparingBody')}</p>
          <p className="text-sm text-ink-3 mb-8">
            {t('callback.attempt', { n: state.attempt, max: MAX_ATTEMPTS })}
          </p>
          <Button variant="secondary" onClick={() => window.location.reload()}>
            {t('callback.retry')}
          </Button>
          {import.meta.env.DEV && state.attempt >= 2 && (
            <p className="text-[12px] text-ink-3 mt-4">
              <a href="/dashboard" className="text-azure-700 border-b border-current pb-px">
                {t('callback.skipDev')}
              </a>
            </p>
          )}
          <p className="text-[13px] text-ink-3 mt-6">
            {t('callback.support')}{' '}
            <a href="mailto:support@fyd.dev" className="text-ink border-b border-current pb-px">
              support@fyd.dev
            </a>
          </p>
        </>
      ) : state.kind === 'me_exhausted' ? (
        <>
          <Eyebrow tone="vermilion" className="mb-3">{t('callback.exhaustedTag')}</Eyebrow>
          <h1 className="font-serif text-3xl font-medium mb-3">
            {t('callback.exhaustedTitle')}
          </h1>
          <p className="text-base text-ink-2 mb-6">{t('callback.exhaustedBody')}</p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            {t('callback.retry')}
          </Button>
          <p className="text-[13px] text-ink-3 mt-6">
            {t('callback.support')}{' '}
            <a href="mailto:support@fyd.dev" className="text-ink border-b border-current pb-px">
              support@fyd.dev
            </a>
          </p>
        </>
      ) : (
        <>
          <Eyebrow tone="vermilion" className="mb-3">{t('callback.errorTag')}</Eyebrow>
          <h1 className="font-serif text-3xl font-medium mb-3">{t('callback.errorTitle')}</h1>
          <p className="text-base text-ink-2 mb-6">{state.message}</p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            {t('callback.retry')}
          </Button>
        </>
      )}
    </div>
  );
}
